import { ChevronRightOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { Grid, Typography } from '@mui/material';
import { GridRowParams, GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { useCurrency } from '@procureai/ui-mui';
import { map } from 'ramda';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { Quote, QuoteLineItemGridField, QuoteLineItemGridRow } from '../../NegotiationQuoteDetailsWidget.types';
import { NULLISH_VALUE } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import { DataGridProStyled, DataGridWidgetHeader } from '~/components/DataGridCustom';
import { RowDetailPanel } from '~/components/DataGridCustom/components/RowDetailPanel/RowDetailPanel';
import { LabeledDataRow } from '~/components/LabeledDataRow/LabeledDataRow';
import { MaskedValue, MaskedValueVariant } from '~/components/MaskedValue';
import { PaymentTerms } from '~/components/PaymentTerms/PaymentTerms';

import { useQuoteDataGridConfig } from './QuoteDetails.dataGrid';

export interface DemandRequestDetailsProps {
  data: Union.Nullable<Quote>;
  loading?: boolean;
  isMasked?: boolean;
}

export const QuoteDetails = ({ data, loading, isMasked }: DemandRequestDetailsProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiationQuoteDetailsWidget' });
  const { format: formatCurrency } = useCurrency();
  const items: QuoteLineItemGridRow[] = map((item) => ({ ...item, isMasked }), data?.lineItems ?? []);

  const apiRef = useGridApiRef();
  const config = useQuoteDataGridConfig();
  const { columnVisibilityModel } = config;

  const skip = loading;

  const getDetailPanelContent = useCallback(<R extends GridValidRowModel = any>({ row }: GridRowParams<R>) => {
    return <RowDetailPanel description={row.description} />;
  }, []);
  const getDetailPanelHeight = useCallback(() => 'auto', []);

  return (
    <>
      <Grid
        container
        direction="row"
        spacing={4}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          alignContent: 'flex-start',
        }}
      >
        <Grid
          container
          spacing={2}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <LabeledDataRow loading={loading} label={t('quoteNo')} body={data?.supplierReference || NULLISH_VALUE} />
          <LabeledDataRow
            loading={loading}
            label={t('total')}
            bodyProps={{
              justifySelf: 'flex-start',
            }}
            body={
              <MaskedValue isMasked={isMasked} variant={MaskedValueVariant.CURRENCY}>
                {formatCurrency(data?.totalValue || 0)}
              </MaskedValue>
            }
          />
        </Grid>
        <Grid
          container
          spacing={2}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <LabeledDataRow
            loading={loading}
            label={t('paymentTerms')}
            body={<PaymentTerms items={data?.paymentTerms} />}
          />
        </Grid>
      </Grid>
      {!skip && items ? (
        <>
          <DataGridWidgetHeader disablePadding left={<Typography variant="body2">{t('positions')}</Typography>} />
          <DataGridProStyled
            {...config}
            apiRef={apiRef}
            autoHeight
            hideFooter
            disableColumnMenu
            loading={loading}
            rows={items}
            getDetailPanelHeight={getDetailPanelHeight}
            getDetailPanelContent={getDetailPanelContent}
            slots={{
              detailPanelExpandIcon: ChevronRightOutlined,
              detailPanelCollapseIcon: ExpandMoreOutlined,
            }}
            sx={{
              '& .MuiDataGrid-row': { cursor: 'default' },
            }}
            initialState={{
              pinnedColumns: { right: [QuoteLineItemGridField.STATUS] },
            }}
            rowSelection={false}
            columnVisibilityModel={columnVisibilityModel}
          />
        </>
      ) : null}
    </>
  );
};
